import { supabase } from './supabase'
import type { MetropolitanItemStatus, MetropolitanItemType } from './metropolitan'
import { sortImportedCharges, type ImportedUnitPreview } from './metropolitanImport'

export interface MetropolitanScheduleItem {
  id: string; competence: string; item_type: MetropolitanItemType; item_number: number; item_total: number
  display_fraction: string; base_amount: string; reference_cub: string; cub_quantity: string; applied_cub: string | null
  updated_amount: string | null; due_date: string; description: string; status: MetropolitanItemStatus
  contract: { id: string; banking_eligible?: boolean; customer: { id: string; name: string; document?: string | null; email?: string | null; phone?: string | null; zip_code?: string | null; address?: string | null; address_number?: string | null; complement?: string | null; neighborhood?: string | null; city?: string | null; state?: string | null; default_due_day?: number; payer_data_complete?: boolean }; unit: { id: string; code: string; enterprise?: string; notes?: string } }
}
export async function loadMetropolitanSchedule(competence: string) {
  const { data, error } = await supabase.from('metropolitan_schedule_items').select('*, contract:metropolitan_contracts!inner(id,banking_eligible, customer:metropolitan_customers!inner(id,name,document,email,phone,zip_code,address,address_number,complement,neighborhood,city,state,default_due_day,payer_data_complete), unit:metropolitan_units!inner(id,code,enterprise,notes))').eq('competence', competence).order('due_date')
  if (error) throw error
  return data as unknown as MetropolitanScheduleItem[]
}

export async function loadMetropolitanUnitCodes() {
  const { data, error } = await supabase.from('metropolitan_units').select('code')
  if (error) throw error
  return (data || []).map(unit => unit.code)
}

export interface MetropolitanScheduleValueItem {
  id: string
  item_type: MetropolitanItemType
  item_number: number
  item_total: number
  display_fraction: string
  base_amount: string | number
  due_date: string
  status: MetropolitanItemStatus
}

export async function loadMetropolitanUnitSchedule(unitId: string) {
  const { data, error } = await supabase.from('metropolitan_schedule_items')
    .select('id,item_type,item_number,item_total,display_fraction,base_amount,due_date,status')
    .eq('unit_id', unitId)
    .order('item_type').order('item_number')
  if (error) throw error
  return data as MetropolitanScheduleValueItem[]
}

export async function deleteMetropolitanScheduleItem(itemId: string) {
  const { data, error } = await supabase.rpc('metropolitan_delete_schedule_item', { p_item_id: itemId, p_reason: 'Exclusão realizada pela Gestão Metropolitan' })
  if (error) throw error
  return data as { deleted_item_id: string; item_type: MetropolitanItemType; remaining_total: number }
}

export async function addMetropolitanScheduleItem(contractId: string, unitId: string, itemType: MetropolitanItemType, dueDate: string, baseAmount: string) {
  const { data, error } = await supabase.rpc('metropolitan_add_schedule_item', {
    p_contract_id: contractId, p_unit_id: unitId, p_item_type: itemType,
    p_due_date: dueDate, p_base_amount: baseAmount, p_reason: 'Inclusão realizada pela Gestão Metropolitan',
  })
  if (error) throw error
  return data as { item_id: string; item_type: MetropolitanItemType; previous_total: number; new_total: number; applied_cub: string | null; updated_amount: string | null }
}

export async function confirmMetropolitanLegacyImport(units: ImportedUnitPreview[]) {
  const payload = units.filter(unit => unit.status === 'NOVA').map(unit => ({
    unit_code: unit.unitCode,
    customer_name: unit.customerName,
    email: unit.email,
    display_fraction: unit.displayFraction,
    reference_cub: unit.referenceCub,
    reference_competence: unit.referenceCompetence,
    charges: sortImportedCharges(unit.charges).map(charge => ({ competence: charge.competence, type: charge.type, base_amount: charge.baseAmount })),
  }))
  const { data, error } = await supabase.rpc('metropolitan_import_legacy_units', { p_units: payload })
  if (error) throw error
  return data as { imported_units: number; ignored_units: number; created_items: number }
}

export interface MetropolitanUnitDeletionPreview {
  unit_id: string
  unit_code: string
  customer_id: string
  customer_name: string
  contract_count: number
  schedule_item_count: number
  charge_count: number
  issued_charge_count: number
  other_unit_count: number
  customer_will_be_deleted: boolean
}

export async function previewMetropolitanUnitDeletion(unitId: string) {
  const { data, error } = await supabase.rpc('metropolitan_unit_deletion_preview', { p_unit_id: unitId })
  if (error) throw error
  return data as MetropolitanUnitDeletionPreview
}

export async function deleteMetropolitanUnit(unitId: string, confirmationCode: string) {
  const { data, error } = await supabase.rpc('metropolitan_delete_unit', {
    p_unit_id: unitId,
    p_confirmation_code: confirmationCode,
  })
  if (error) throw error
  return data as MetropolitanUnitDeletionPreview
}
export async function previewCub(competence: string, amount: string, confirm = false) {
  const { data, error } = await supabase.rpc('metropolitan_recalculate_competence', { p_competence: competence, p_cub: amount, p_confirm: confirm })
  if (error) throw error
  return data as { item_id: string; old_amount: string | null; new_amount: string }[]
}

export async function prepareMetropolitanBankBatch(competence: string, itemIds: string[], overrideDueDate?: string, overrideAmount?: string) {
  const { data, error } = await supabase.rpc('metropolitan_prepare_bank_batch', {
    p_competence: competence,
    p_item_ids: itemIds,
    p_override_due_date: overrideDueDate || null,
    p_override_amount: overrideAmount || null,
  })
  if (error) throw error
  return data as string
}

export interface MetropolitanBatchCharge {
  id: string
  control_code: string | null
  amount: string
  due_date: string
  description: string
  status: string
  snapshot: {
    customer_name?: string
    unit?: string
    contract?: string
    competence?: string
    type?: MetropolitanItemType
    amount?: string | number
    schedule_amount?: string | number
    amount_overridden?: boolean
    due_date?: string
    description?: string
  }
}

export interface MetropolitanBatchReview {
  id: string
  competence: string
  status: string
  created_at: string
  charges: MetropolitanBatchCharge[]
}

export async function loadMetropolitanBatchReview(batchId: string): Promise<MetropolitanBatchReview> {
  const { data: batch, error: batchError } = await supabase.from('metropolitan_batches').select('id,competence,status,created_at').eq('id', batchId).single()
  if (batchError) throw batchError
  const { data: charges, error: chargesError } = await supabase.from('metropolitan_bank_charges').select('id,control_code,amount,due_date,description,status,snapshot').eq('batch_id', batchId).eq('status', 'PREPARADA').order('due_date')
  if (chargesError) throw chargesError
  return { ...batch, charges: charges || [] } as MetropolitanBatchReview
}

export async function confirmMetropolitanBankBatch(batchId: string) {
  const { error } = await supabase.rpc('metropolitan_confirm_bank_batch', { p_batch_id: batchId })
  if (error) throw error
}
